import { FC } from 'react';

import { Environment } from '../../constants/environments';
import { ImagesApp } from '../../constants/images';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import * as Styles from './styles';
import type { ResaleSoldOutVariant } from './styles';

export type { ResaleSoldOutVariant } from './styles';

// The explore variant leaves the checkout via a link (<a href>), with no handler; the others
// act within the app and take onCtaClick.
type ResaleSoldOutStateProps =
  | { variant: 'explore' }
  | { variant: Exclude<ResaleSoldOutVariant, 'explore'>; onCtaClick: () => void };

const I18N_PREFIX = ['RESALE_LISTING', 'SOLD_OUT_STATE'];

const VARIANT_CONTENT: Record<ResaleSoldOutVariant, {
  icon: string;
  descriptionKey: string;
  ctaKey: string;
  ctaId: string;
}> = {
  waitlist : {
    icon : ImagesApp.svg.resaleSoldOutWaitlist,
    descriptionKey : 'WAITLIST_DESCRIPTION',
    ctaKey : 'WAITLIST_CTA',
    ctaId : 'waitlist-cta-button',
  },
  listing : {
    icon : ImagesApp.svg.resaleSoldOutListing,
    descriptionKey : 'LISTING_DESCRIPTION',
    ctaKey : 'LISTING_CTA',
    ctaId : 'resale-cta-button',
  },
  explore : {
    // Intentional: in Figma, "sold out without resale" (NEWB-903) uses the same icon as "listing".
    icon : ImagesApp.svg.resaleSoldOutListing,
    descriptionKey : 'EXPLORE_DESCRIPTION',
    ctaKey : 'EXPLORE_CTA',
    ctaId : 'explore-events-cta-button',
  },
};

/**
 * "Official sale sold out" state on the tickets page, with one CTA per scenario.
 * - waitlist: resale not open yet → CTA opens the waitlist.
 * - listing: resale already open → CTA goes to the resale listing.
 * - explore: sold out with no resale or waitlist → CTA is a link to the site
 *   (Environment.SITE_URL), where the other events are.
 * Which scenario to show is decided in `pages/tickets.tsx`.
 */
const ResaleSoldOutState: FC<ResaleSoldOutStateProps> = (props) => {
  const { variant } = props;
  const { translate } = useI18nContext();
  const { icon, descriptionKey, ctaKey, ctaId } = VARIANT_CONTENT[variant];
  const ctaLabel = translate([...I18N_PREFIX, ctaKey]);

  return (
    <Styles.Container
      id="resale-sold-out-state"
      data-testid="resale-sold-out-state"
      data-variant={variant}
    >
      <Styles.Icon
        src={icon}
        alt=""
        aria-hidden="true"
        id="resale-sold-out-icon"
        data-testid="resale-sold-out-icon"
        $variant={variant}
      />
      <Styles.Content>
        <Styles.Texts $variant={variant}>
          <Styles.Title id="resale-sold-out-title">
            {translate([...I18N_PREFIX, 'TITLE'])}
          </Styles.Title>
          <Styles.Description id="resale-sold-out-description">
            {translate([...I18N_PREFIX, descriptionKey])}
          </Styles.Description>
        </Styles.Texts>
        {props.variant === 'explore' ? (
          <Styles.CtaLink href={Environment.SITE_URL} id={ctaId}>
            {ctaLabel}
          </Styles.CtaLink>
        ) : (
          <Styles.CtaButton type="button" id={ctaId} onClick={props.onCtaClick}>
            {ctaLabel}
          </Styles.CtaButton>
        )}
      </Styles.Content>
    </Styles.Container>
  );
};

export default ResaleSoldOutState;
